import type { SavedAnswer } from 'shared';

/**
 * session token อยู่ใน sessionStorage (หมดอายุเมื่อปิดแท็บ ตาม docs/07-security.md)
 * ส่วน draft คำตอบอยู่ใน localStorage เพื่อให้ทนต่อเน็ตหลุด/ปิดแท็บโดยไม่ตั้งใจ
 */
const SESSION_KEY = 'exam-session-token';
const DRAFT_PREFIX = 'exam-draft-';

export type AnswerDraft = {
  selectedOptionIds: string[];
  textAnswer: string | null;
  isFlagged: boolean;
};

export type DraftPayload = {
  attemptId: string;
  updatedAt: number;
  answers: Record<string, AnswerDraft>;
};

function safeStorage(storage: Storage): Storage | null {
  try {
    const probe = '__exam_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    // โหมดส่วนตัวของบางเบราว์เซอร์ปิด storage ไว้ — ระบบต้องยังทำงานได้
    return null;
  }
}

export function readSessionToken(): string | null {
  return safeStorage(sessionStorage)?.getItem(SESSION_KEY) ?? null;
}

export function writeSessionToken(token: string): void {
  safeStorage(sessionStorage)?.setItem(SESSION_KEY, token);
}

export function clearSessionToken(): void {
  safeStorage(sessionStorage)?.removeItem(SESSION_KEY);
}

export function readDraft(attemptId: string): DraftPayload | null {
  const raw = safeStorage(localStorage)?.getItem(`${DRAFT_PREFIX}${attemptId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DraftPayload;
    return parsed.attemptId === attemptId ? parsed : null;
  } catch {
    return null;
  }
}

export function writeDraft(payload: DraftPayload): void {
  safeStorage(localStorage)?.setItem(`${DRAFT_PREFIX}${payload.attemptId}`, JSON.stringify(payload));
}

export function clearDraft(attemptId: string): void {
  safeStorage(localStorage)?.removeItem(`${DRAFT_PREFIX}${attemptId}`);
}

export function draftFromSavedAnswers(answers: SavedAnswer[]): Record<string, AnswerDraft> {
  const result: Record<string, AnswerDraft> = {};
  for (const answer of answers) {
    result[answer.questionId] = {
      selectedOptionIds: answer.selectedOptionIds,
      textAnswer: answer.textAnswer,
      isFlagged: answer.isFlagged,
    };
  }
  return result;
}

/**
 * ลายเซ็นของชุดคำตอบ — ใช้เทียบว่าสิ่งที่อยู่ในหน้าจอต่างจากที่ server รับไปแล้วหรือยัง
 * เรียงคีย์ให้คงที่ก่อนเสมอ ลำดับของ object หรือของตัวเลือกที่เลือกจึงไม่ทำให้ผลเพี้ยน
 * (ถ้าเทียบด้วย reference จะยิงบันทึกซ้ำทั้งที่คำตอบเหมือนเดิม)
 */
export function answersSignature(answers: Record<string, AnswerDraft>): string {
  const entries = Object.entries(answers)
    .filter(([, draft]) => isAnswered(draft) || draft.isFlagged)
    .map(([questionId, draft]) => [
      questionId,
      [...draft.selectedOptionIds].sort(),
      (draft.textAnswer ?? '').trim(),
      draft.isFlagged,
    ])
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

  return JSON.stringify(entries);
}

export function isAnswered(draft: AnswerDraft | undefined): boolean {
  if (!draft) return false;
  if (draft.selectedOptionIds.length > 0) return true;
  return (draft.textAnswer ?? '').trim().length > 0;
}
